/**
 * Reusable client multi-select component
 * Consolidates client selection UI logic
 */

import React from "react";
import { ResponsiveCommandMultiSelect } from "@/components/ui/responsive-select";
import type { ClientRecord, GrantAccessClientSelectProps } from "@/lib/types/auth/grant-access";
import { formatClientName, createClientSearchableText } from "@/lib/types/auth/grant-access";

export function GrantAccessClientSelect({
  value,
  onValueChange,
  options,
  searchValue,
  onSearchChange,
  isSearching,
  isLoading,
  disabled,
  grantToAllClients,
  isBulk = false,
}: GrantAccessClientSelectProps) {
  const clientOptions = React.useMemo(
    () =>
      options.map((client: ClientRecord) => ({
        value: client.id,
        label: formatClientName(client),
        searchableText: createClientSearchableText(client),
      })),
    [options]
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label className="block text-sm font-medium">
          Clients {!grantToAllClients && <span className="text-red-500">*</span>}
        </label>
      </div>
      <ResponsiveCommandMultiSelect
        value={value.filter((v: any) => typeof v === "string")}
        onValueChange={onValueChange}
        options={clientOptions}
        placeholder={
          grantToAllClients ? "All clients will be selected" : "Search and select clients..."
        }
        disabled={isLoading || disabled || grantToAllClients}
        className="w-full"
        searchValue={searchValue}
        onSearchChange={onSearchChange}
        isSearching={isSearching}
        title="Select Clients"
        description={
          isBulk
            ? "Search and pick one or more clients to grant access to."
            : "Search and pick one or more clients."
        }
        searchPlaceholder="Search clients..."
        forceDrawer={false}
        highZIndex
        enableRecents
        recentKey="clients"
      />
      <p className="text-xs text-muted-foreground mt-1">
        Type to search by name, company, or email. You can select multiple.
      </p>
    </div>
  );
}
